import {api, routes} from "@/plugins/axios";
import {ref} from "vue";
import {useCometChat} from "@/composables/useCometChat";

export function useChat() {
    const fetching = ref(false);

    async function getChatAccessToken(orgSlug) {
        fetching.value = true;
        try {
            const response = await api.get(routes.chat.getAccessToken(orgSlug));
            return response.data;
        } finally {
            fetching.value = false;
        }
    }

    async function loginWithToken(CometChat, resolveToken) {
        const token = await resolveToken();

        if (!token) {
            return null;
        }

        try {
            return await CometChat.login(token);
        } catch (error) {
            if (error.name === 'LOGIN_IN_PROGRESS') {
                throw error;
            }

            const freshToken = await resolveToken({ refresh: true });

            if (!freshToken) {
                throw error;
            }

            return CometChat.login(freshToken);
        }
    }

    async function doChatLogin(resolveToken) {
        const { getInstance } = useCometChat();

        const CometChat = await getInstance();

        try {
            const user = await CometChat.getLoggedinUser();

            if (user) {
                return user;
            }

            return await loginWithToken(CometChat, resolveToken);
        } catch (error) {
            if (error.name === 'LOGIN_IN_PROGRESS') {
                // A concurrent login (another call or browser tab) is still in
                // flight. A single fixed wait often resolved before that login
                // finished, throwing spuriously. Poll a few times before giving up.
                const MAX_ATTEMPTS = 5;
                const RETRY_DELAY_MS = 500;

                for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
                    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));

                    const user = await CometChat.getLoggedinUser();
                    if (user) {
                        return user;
                    }
                }

                throw new Error('Login in progress but no user found after waiting');
            }
            throw error;
        }
    }

    return {
        getChatAccessToken,
        doChatLogin,
        fetching,
    }
}
